import React, { useState, useEffect } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { Button } from '../layout/buttons';
import RadioButtonGroup from '../layout/inputs/RadioButtonGroup';
import CompanySelector from './CompanySelector';
import StakeholderSelector from './StakeholderSelector';
import { fetchUserCompanies } from '../../utils/companies';
import { fetchUserStakeholders } from '../../utils/stakeholders';
import { logger } from '../../utils/logger';
import styles from './Canvasses.module.css';

// Context type interface
const CanvassContext = {
  type: 'company' | 'stakeholder' | 'freeform',
  companyId: undefined,
  stakeholderId: undefined,
  contextData: undefined
};

const CanvassCreationFlow = ({ onContextSelect, onProceed }) => {
  const [contextType, setContextType] = useState('freeform');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);
  const [userCompanies, setUserCompanies] = useState([]);
  const [companyStakeholders, setCompanyStakeholders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [companiesResult, stakeholdersResult] = await Promise.all([
        fetchUserCompanies({ pageSize: 50 }),
        fetchUserStakeholders({ pageSize: 100 })
      ]);
      
      setUserCompanies(companiesResult.companies || []);
      setCompanyStakeholders(stakeholdersResult.stakeholders || []);
    } catch (err) {
      logger.error('Error loading initial data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  
  const handleContextTypeChange = (type) => {
    setContextType(type);
    setSelectedCompany(null);
    setSelectedStakeholder(null);
    setError('');
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setSelectedStakeholder(null);
    setError('');
  };

  const handleStakeholderSelect = (stakeholder) => {
    setSelectedStakeholder(stakeholder);
    setSelectedCompany(null);
    setError('');
  };

  
  const handleProceed = () => {
    let context;
    
    if (contextType === 'freeform') {
      context = {
        type: 'freeform',
        companyId: undefined,
        stakeholderId: undefined,
        contextData: null
      };
    } else if (contextType === 'company' && selectedCompany) {
      context = {
        type: 'company',
        companyId: selectedCompany.id,
        stakeholderId: undefined,
        contextData: selectedCompany
      };
    } else if (contextType === 'stakeholder' && selectedStakeholder) {
      context = {
        type: 'stakeholder',
        companyId: undefined,
        stakeholderId: selectedStakeholder.id,
        contextData: selectedStakeholder
      };
    } else {
      setError('Please make a selection before proceeding');
      return;
    }
    
    onContextSelect(context);
    onProceed();
  };

  const renderContextSelection = () => (
    <div className={styles.contextSelection}>
      <h3>Choose canvass context:</h3>
      
      <RadioButtonGroup
        value={contextType}
        onChange={handleContextTypeChange}
        options={[
          { value: 'company', label: 'Organization' },
          // { value: 'stakeholder', label: 'Stakeholder' },
          { value: 'freeform', label: 'Freeforms' }
        ]}
        className={styles.contextRadioGroup}
      />
      
      {error && <div className={styles.errorMessage}>{error}</div>}
      
      {contextType === 'company' && (
        <div className={styles.contextPanel}>
          <CompanySelector 
            onSelect={handleCompanySelect}
            userCompanies={userCompanies}
          />
        </div>
      )}
      
      {contextType === 'stakeholder' && (
        <div className={styles.contextPanel}>
          <StakeholderSelector
            onSelect={handleStakeholderSelect}
            companyStakeholders={companyStakeholders}
          />
        </div>
      )}
      
      {contextType === 'freeform' && (
        <div className={styles.contextPanel}>
          <div className={styles.freeformInfo} style={{ textAlign: 'left' }}>
            <h4>Freeform Canvass</h4>
            <p>Create a canvass without any specific organization or stakeholder context. This gives you maximum flexibility for general-purpose canvassing.</p>
            <ul style={{ textAlign: 'left' }}>
              <li>Custom objectives and parameters</li>
              <li>Full creative control</li>
            </ul>
          </div>
        </div>
      )}
      
      <div className={styles.contextActions}>
        <Button 
          variant="primary" 
          onClick={handleProceed}
          disabled={loading || (
            contextType === 'company' && !selectedCompany ||
            contextType === 'stakeholder' && !selectedStakeholder
          )}
        >
          Proceed to Canvass Creation
        </Button>
      </div>
    </div>
  );

  if (loading && userCompanies.length === 0) {
    return <div>Loading data...</div>;
  }

  return (
    <div className={styles.canvassCreationFlow}>
      {renderContextSelection()}
    </div>
  );
};

export default CanvassCreationFlow;
